// ============================================================================
// ehrCandidateProvenance.js — Build 2J P2 (pure, ESM)
//
// EHR/FHIR candidates are SOURCE MATERIAL, not referral truth. The Gate shows
// them, the GP confirms/edits/rejects, and Gate Submit writes the confirmed truth.
// This module produces NEVER-PHI audit provenance proving WHICH EHR candidate
// REFERENCES influenced the submitted referral — refs / field keys / enums /
// counts ONLY. It never carries a candidate VALUE, display text, or raw FHIR.
//
// Pure: no Firebase / network / logging / PHI. Two responsibilities:
//   deriveUsedCandidateRefs(...)   — from Gate EHR-suggestion field state → used refs
//   buildEhrCandidateProvenance(.) — validate + shape the never-PHI audit payload
//
// HARD RULES enforced here:
//   - candidateId is an opaque ref (charset + length capped).
//   - targetKey MUST be a known EHR candidate field key (allowlist) — so it is a
//     structural key, NEVER a PHI value.
//   - action MUST be one of the three enums.
//   - the builder copies ONLY { candidateId, targetKey, action } from each input
//     ref — a candidate's normalizedValue / displayLabel / reviewedValue can never
//     leak even if the caller passes a full candidate object.
//   - counts are numbers only.
//   - excessive / malformed input throws EHR_PROVENANCE_VIOLATION (fail-fast).
// ============================================================================

export const EHR_PROVENANCE_SCHEMA_VERSION = 1;
export const EHR_PROVENANCE_SOURCE = 'ehr_suggestions';

// Action an EHR candidate ref took on the submitted referral.
export const EHR_PROVENANCE_ACTION = Object.freeze({
  ACCEPTED_AS_SUGGESTED: 'ACCEPTED_AS_SUGGESTED', // submitted as originally populated from the candidate
  EDITED_AFTER_PREFILL: 'EDITED_AFTER_PREFILL',   // used as starting material, then changed before submit
  MANUALLY_APPLIED: 'MANUALLY_APPLIED',           // explicitly applied/accepted into the field
});
const VALID_ACTIONS = new Set(Object.values(EHR_PROVENANCE_ACTION));

// Canonical EHR candidate field keys — MIRROR of
// functions/fhir/ehrCandidateReview.js SUPPORTED_EHR_FIELD_KEYS. A targetKey must
// be one of these, guaranteeing it is a structural form-field key, never a value.
export const SUPPORTED_EHR_TARGET_KEYS = Object.freeze([
  'patient.name', 'patient.dateOfBirth', 'patient.sex', 'patient.identifier',
  'contact.phone', 'contact.email', 'contact.address',
  'insurance.payer', 'insurance.payerId', 'insurance.memberId',
  'insurance.groupNumber', 'insurance.planType', 'insurance.planName',
]);
const SUPPORTED_TARGET_KEY_SET = new Set(SUPPORTED_EHR_TARGET_KEYS);

// Caps (defence against unbounded / injected input in a Never-PHI doc).
export const MAX_USED_CANDIDATE_REFS = 50;
const MAX_CANDIDATE_ID_LEN = 128;
const MAX_TARGET_KEY_LEN = 64;
const MAX_PACKET_ID_LEN = 128;
const MAX_UID_LEN = 128;
const MAX_IGNORED_COUNT = 100000;

// Opaque server ids (candidateId 'ehrcand_<uuid>', packetId, uid): alphanumeric +
// _ . : - only. Blocks whitespace, '@', and free-text — so names/emails/addresses
// can never sit in an id slot.
const OPAQUE_ID_RE = /^[A-Za-z0-9_.:-]+$/;

export const EHR_PROVENANCE_VIOLATION = 'EHR_PROVENANCE_VIOLATION';

export class EhrCandidateProvenanceViolation extends Error {
  constructor(reason) {
    super(`${EHR_PROVENANCE_VIOLATION}: ${reason}`);
    this.name = 'EhrCandidateProvenanceViolation';
    this.code = EHR_PROVENANCE_VIOLATION; // typed code for fail-fast UI mapping
  }
}

function safeOpaqueId(v, max, label) {
  if (typeof v !== 'string') throw new EhrCandidateProvenanceViolation(`${label} must be a string`);
  const t = v.trim();
  if (!t || t.length > max) throw new EhrCandidateProvenanceViolation(`${label} has an invalid length`);
  if (!OPAQUE_ID_RE.test(t)) throw new EhrCandidateProvenanceViolation(`${label} has an unsafe character`);
  return t;
}

/**
 * Derive the list of USED EHR candidate refs (+ ignored/rejected count) from the
 * Gate EHR-suggestions field state. Pure — no DOM, no Firestore.
 *
 * @param {{ fieldState: object, getValue?: (k:string)=>any, autoFill?: boolean }} args
 *   fieldState — keyed by targetKey → { candidateId, status, baseline?, noField?, reviewStatus? }
 *   getValue   — returns the CURRENT form value for a field key (decides edited-vs-unchanged)
 *   autoFill   — true when EHR values were prefilled as editable drafts (Build 2N)
 * @returns {{ usedCandidateRefs: Array<{candidateId,targetKey,action}>, ignoredOrRejectedCount: number }}
 */
export function deriveUsedCandidateRefs({ fieldState, getValue, autoFill } = {}) {
  const fs = fieldState && typeof fieldState === 'object' ? fieldState : {};
  const getVal = typeof getValue === 'function' ? getValue : () => undefined;
  const used = [];
  let ignoredOrRejected = 0;

  for (const targetKey of Object.keys(fs)) {
    const s = fs[targetKey];
    if (!s || !s.candidateId) continue;
    if (s.status === 'dismissed') { ignoredOrRejected += 1; continue; }     // GP rejected
    if (s.noField) { ignoredOrRejected += 1; continue; }                    // shown, no matching field
    if (s.status !== 'confirmed') { ignoredOrRejected += 1; continue; }     // pending / not used

    const cur = getVal(targetKey);
    if (cur == null || String(cur) === '') { ignoredOrRejected += 1; continue; } // GP cleared it

    const hasBaseline = Object.prototype.hasOwnProperty.call(s, 'baseline');
    const changed = hasBaseline ? (cur !== s.baseline) : (s.reviewStatus === 'EDITED');
    let action;
    if (autoFill) {
      action = changed ? EHR_PROVENANCE_ACTION.EDITED_AFTER_PREFILL : EHR_PROVENANCE_ACTION.ACCEPTED_AS_SUGGESTED;
    } else {
      action = changed ? EHR_PROVENANCE_ACTION.EDITED_AFTER_PREFILL : EHR_PROVENANCE_ACTION.MANUALLY_APPLIED;
    }
    used.push({ candidateId: s.candidateId, targetKey, action });
  }

  return { usedCandidateRefs: used, ignoredOrRejectedCount: ignoredOrRejected };
}

/**
 * Build the NEVER-PHI EHR candidate provenance audit payload, or null when no EHR
 * candidate influenced the referral (non-EHR submits stay unchanged). Throws
 * EhrCandidateProvenanceViolation on any unsafe / malformed input — the caller
 * fails fast BEFORE any write.
 *
 * Only { candidateId, targetKey, action } is copied from each input ref; any other
 * key (e.g. normalizedValue / displayLabel) is dropped and never reaches the doc.
 *
 * @param {{ packetId: string, capturedByUid: string, capturedAt?: *,
 *   usedCandidateRefs: Array<object>, ignoredOrRejectedCount?: number }} args
 * @returns {object|null}
 */
export function buildEhrCandidateProvenance({
  packetId, capturedByUid, capturedAt = null, usedCandidateRefs, ignoredOrRejectedCount = 0,
} = {}) {
  if (!Array.isArray(usedCandidateRefs) || usedCandidateRefs.length === 0) {
    return null; // no EHR candidate influenced the referral → omit provenance entirely
  }
  if (usedCandidateRefs.length > MAX_USED_CANDIDATE_REFS) {
    throw new EhrCandidateProvenanceViolation(`too many candidate refs (> ${MAX_USED_CANDIDATE_REFS})`);
  }

  const counts = { used: 0, acceptedAsSuggested: 0, editedAfterPrefill: 0, manuallyApplied: 0, ignoredOrRejected: 0 };
  const refs = [];
  for (const r of usedCandidateRefs) {
    if (!r || typeof r !== 'object') throw new EhrCandidateProvenanceViolation('candidate ref must be an object');
    const candidateId = safeOpaqueId(r.candidateId, MAX_CANDIDATE_ID_LEN, 'candidateId');

    if (typeof r.targetKey !== 'string') throw new EhrCandidateProvenanceViolation('targetKey must be a string');
    const targetKey = r.targetKey.trim();
    if (!targetKey || targetKey.length > MAX_TARGET_KEY_LEN) throw new EhrCandidateProvenanceViolation('targetKey has an invalid length');
    if (!SUPPORTED_TARGET_KEY_SET.has(targetKey)) throw new EhrCandidateProvenanceViolation('targetKey is not a supported EHR field key');

    if (!VALID_ACTIONS.has(r.action)) throw new EhrCandidateProvenanceViolation('unknown action');

    // ONLY the three safe keys are copied — a value/display/raw-FHIR field on the
    // input object can never reach the audit doc.
    refs.push({ candidateId, targetKey, action: r.action });

    if (r.action === EHR_PROVENANCE_ACTION.ACCEPTED_AS_SUGGESTED) counts.acceptedAsSuggested += 1;
    else if (r.action === EHR_PROVENANCE_ACTION.EDITED_AFTER_PREFILL) counts.editedAfterPrefill += 1;
    else counts.manuallyApplied += 1;
  }
  counts.used = refs.length;

  const ig = Number(ignoredOrRejectedCount);
  counts.ignoredOrRejected = Number.isFinite(ig) && ig > 0 ? Math.min(Math.floor(ig), MAX_IGNORED_COUNT) : 0;

  return {
    schemaVersion: EHR_PROVENANCE_SCHEMA_VERSION,
    packetId: safeOpaqueId(packetId, MAX_PACKET_ID_LEN, 'packetId'),
    source: EHR_PROVENANCE_SOURCE,
    capturedAt: capturedAt == null ? null : capturedAt, // serverTimestamp sentinel from caller (plain in tests)
    capturedByUid: safeOpaqueId(capturedByUid, MAX_UID_LEN, 'capturedByUid'),
    usedCandidateRefs: refs,
    counts,
  };
}

export default {
  EHR_PROVENANCE_SCHEMA_VERSION,
  EHR_PROVENANCE_SOURCE,
  EHR_PROVENANCE_ACTION,
  SUPPORTED_EHR_TARGET_KEYS,
  MAX_USED_CANDIDATE_REFS,
  EHR_PROVENANCE_VIOLATION,
  EhrCandidateProvenanceViolation,
  deriveUsedCandidateRefs,
  buildEhrCandidateProvenance,
};
